import React, { useEffect, useState } from "react";
import { formatDays, formatLeaveRange, leaveDays } from "../core/leaveDates";

/** The reason the backend stamps on every booking a leave approval cancels. */
export const LEAVE_CANCEL_REASON = "Coach is on leave";

const firstName = (name) => String(name || "").trim().split(/\s+/)[0] || "This coach";
const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

const bookingDate = (iso) =>
  new Date(iso).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });
const bookingTime = (iso) =>
  new Date(iso).toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit" });

/**
 * Approve / reject a coach's leave (Claude Design "Manager Leaves" 5a).
 *
 * Three shapes:
 * - approve, no bookings — "Approve {name}'s leave?"
 * - approve with bookings — lists them and the chat card each client gets,
 *   because approving cancels them server side
 * - reject — a reason is required
 *
 * @param {Object} props
 * @param {Object|null} props.leave - The row being decided (with `bookings`), or null when closed.
 * @param {'approved'|'rejected'} props.decision
 * @param {string} props.approverName - The signed-in manager, as the chat card names them.
 * @param {Function} props.onClose
 * @param {(note: string) => Promise<void>} props.onConfirm - Rejects with an Error to show.
 */
const LeaveDecisionModal = ({ leave, decision, approverName, onClose, onConfirm }) => {
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setNote("");
    setError("");
    setSaving(false);
  }, [leave?.id, decision]);

  if (!leave) return null;

  const reject = decision === "rejected";
  const bookings = Array.isArray(leave.bookings) ? leave.bookings : [];
  const conflict = !reject && bookings.length > 0;
  const first = firstName(leave.employee_name);
  const dates = formatLeaveRange(leave, "–");
  const n = bookings.length;

  const title = conflict
    ? `${first} has ${plural(n, "booking")} during this leave`
    : reject
      ? `Reject ${first}'s leave?`
      : `Approve ${first}'s leave?`;
  const subtitle = conflict
    ? `Do you want to cancel these bookings and approve leave for ${dates}?`
    : [leave.leave_type, dates, formatDays(leaveDays(leave))].filter(Boolean).join(" · ");
  const confirmLabel = conflict
    ? `Cancel ${plural(n, "booking")} & approve`
    : reject
      ? "Reject leave"
      : "Approve leave";
  const clientMessage = `${approverName || "Your manager"} cancelled your call with ${
    leave.employee_name || "your coach"
  }. Reason: ${LEAVE_CANCEL_REASON}`;

  const handleConfirm = async () => {
    const trimmed = note.trim();
    if (reject && !trimmed) {
      setError("Add a note so the coach knows why.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await onConfirm(trimmed);
    } catch (err) {
      setError(err?.message || "Couldn't save this decision. Try again.");
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-[rgba(31,35,40,0.45)] flex items-center justify-center p-4"
      onClick={saving ? undefined : onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="leave-decision-title"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-[540px] max-h-[90vh] overflow-y-auto bg-white rounded-[14px] shadow-[0_24px_60px_rgba(31,35,40,0.3)] p-6 flex flex-col gap-4 text-[#1F2328]"
      >
        <div className="flex flex-col gap-1">
          <h2 id="leave-decision-title" className="text-[19px] font-extrabold">
            {title}
          </h2>
          <span className="text-sm text-[#5C554F]">{subtitle}</span>
        </div>

        {!reject && leave.bookings == null && (
          <div className="text-[13px] font-semibold text-[#9A5B00] bg-[#FFF4DB] rounded-lg px-3 py-2.5">
            Couldn't check {first}'s bookings. Approving cancels any client bookings during this leave
            and messages those clients.
          </div>
        )}

        {conflict && (
          <>
            <div className="border border-[#EFEBE7] rounded-[10px] overflow-hidden">
              {bookings.map((b) => (
                <div
                  key={b.id}
                  className="grid grid-cols-[100px_76px_minmax(0,1fr)] gap-2.5 px-3.5 py-2.5 text-sm border-b border-[#F4F1EE] last:border-b-0"
                >
                  <span className="font-bold">{bookingDate(b.start_time)}</span>
                  <span>{bookingTime(b.start_time)}</span>
                  <span className="truncate">
                    {b.user_name || "Client"}
                    {b.duration_minutes ? (
                      <span className="text-[#8A8178]"> · {b.duration_minutes} min</span>
                    ) : null}
                  </span>
                </div>
              ))}
            </div>
            <div className="flex flex-col gap-1.5">
              <span className="text-xs font-bold text-[#8A8178] uppercase tracking-[0.06em]">
                Automated message to each client
              </span>
              <div className="bg-[#FAF8F6] rounded-[10px] px-3.5 py-3 text-sm leading-relaxed">
                <span className="block font-bold">Call cancelled</span>
                {clientMessage}
              </div>
            </div>
          </>
        )}

        <label className="flex flex-col gap-1.5">
          <span className="text-[13px] font-bold">
            {reject ? "Reason for rejecting" : "Note to coach (optional)"}
          </span>
          <textarea
            value={note}
            onChange={(e) => {
              setNote(e.target.value);
              setError("");
            }}
            rows={3}
            placeholder={
              reject ? "e.g. We are short-staffed that week, can you move it?" : "e.g. Enjoy the time off."
            }
            className={`border rounded-lg px-3 py-2.5 text-sm resize-none outline-none focus:shadow-[0_0_0_3px_#FFE3D2] ${
              error ? "border-[#B42318]" : "border-[#E8E5E1] focus:border-[#F2601F]"
            }`}
          />
          {error && (
            <span role="alert" className="text-[#B42318] text-[13px] font-semibold">
              {error}
            </span>
          )}
        </label>

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="h-10 px-4 rounded-lg border border-[#E8E5E1] bg-white text-sm font-bold hover:bg-[#F4F1EE]"
          >
            {conflict ? "Not now" : "Cancel"}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={saving}
            className={`h-10 px-4 rounded-lg text-white text-sm font-bold disabled:opacity-70 ${
              reject ? "bg-[#B42318] hover:bg-[#912018]" : "bg-[#F2601F] hover:bg-[#E8590C]"
            }`}
          >
            {saving ? "Saving…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LeaveDecisionModal;
